/**
 * Formato único do resultado de uma avaliação, consumido tanto pelo modal enxuto
 * (ResultModal) quanto pela página de interpretação completa (/avaliacao/:token).
 *
 * Duas origens possíveis:
 *  - 'local': acabou de ser calculada nesta aba (useReducer de guidedFlow.js). Tem os 11
 *    blocos e os itens marcados, então a leitura por bloco e a régua funcionam.
 *  - 'saved': veio do histórico do paciente (NCB). Só guarda o escore por domínio (7),
 *    nunca os 11 blocos nem os itens — então `checked`/`batch`/`blockScores` ficam `null`
 *    de propósito, em vez de fingir uma granularidade que o registro não tem.
 */
import { MEEM_TOTAL_MAX } from './meemItems';
import { blockScoresFrom, domainScoresFrom } from './guidedFlow';

export function buildLocalSnapshot({ total, education, educationAnswered, reference, ageConfirm, checked = {}, batch = {}, mode, unevaluated = 0 }) {
  const domains = mode === 'domains';
  const blockScores = domains ? blockScoresFrom(checked) : null;
  return {
    source: 'local',
    total,
    totalMax: MEEM_TOTAL_MAX,
    mode,
    education,
    educationAnswered,
    reference,
    ageConfirm,
    unevaluated: domains ? unevaluated : 0,
    checked: domains ? checked : null,
    batch: domains ? batch : null,
    blockScores,
    domainScores: domains ? domainScoresFrom(blockScores) : null,
    createdAt: new Date().toISOString()
  };
}

/** Mesmo parser defensivo de ManagementAssessmentForm.jsx: `answers_json` chega como string. */
function parseAnswers(value) {
  if (!value) return {};
  if (typeof value === 'object' && !Array.isArray(value)) return value;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

export function buildSavedSnapshot(assessment) {
  const answers = parseAnswers(assessment?.answers_json);
  const domains = answers.mode === 'domains' && answers.blockScores && Object.keys(answers.blockScores).length > 0;
  return {
    source: 'saved',
    total: Number(assessment?.score) || 0,
    totalMax: MEEM_TOTAL_MAX,
    mode: domains ? 'domains' : 'total',
    education: answers.education || 'none',
    educationAnswered: true,
    reference: assessment?.reference_key || 'none',
    ageConfirm: answers.ageConfirm || 'not_informed',
    unevaluated: null,
    checked: null,
    batch: null,
    blockScores: null,
    domainScores: domains ? answers.blockScores : null,
    createdAt: assessment?.applied_at || assessment?.created_at || null
  };
}
